import React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  TextField,
  Button,
  Paper,
  Breadcrumbs,
  Link,
  MenuItem,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

interface Location {
  value: string;
  label: string;
}

interface TripDetails {
  tripType: "Single" | "Round";
  journeyDates: {
    startDate: string;
    returnDate: string;
  };
  fromLocation: string;
  toLocation: string;
  otherFromLocation: string;
  otherToLocation: string;
  journeyStartOdometerReading: string;
  journeyEndOdometerReading: string;
}

interface CreateTripProps {
  vehicleDetails: any;
  tripDetails: TripDetails;
  setTripDetails: React.Dispatch<React.SetStateAction<any>>;
  locations: Location[];
  isEditMode: boolean;
}

const CreateTrip: React.FC<CreateTripProps> = ({
  vehicleDetails,
  tripDetails,
  setTripDetails,
  locations,
  isEditMode,
}: any) => {
  const handleTripTypeChange = (
    event: React.MouseEvent<HTMLElement>,
    newTripType: "Single" | "Round"
  ) => {
    if (newTripType !== null) {
      setTripDetails((prev: any) => ({
        ...prev,
        tripType: newTripType,
      }));
    }
  };

  return (
    <Paper sx={{ p: 3, mb: 4, mt: 4 }}>
      <Typography variant="h6" gutterBottom>
        Trip Details
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={12}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {/* <Typography component="span" sx={{ mr: 2 }}>
              Type
            </Typography> */}
            <ToggleButtonGroup
              value={tripDetails?.tripType}
              exclusive
              onChange={handleTripTypeChange}
              aria-label="trip type"
              disabled={isEditMode}
            >
              <ToggleButton
                value="Round"
                aria-label="round trip"
                sx={{
                  "&.Mui-selected": {
                    backgroundColor: "#000",
                    color: "#fff",
                    "&:hover": {
                      backgroundColor: "#000",
                    },
                  },
                  textTransform: "none",
                }}
              >
                Round Trip
              </ToggleButton>
              <ToggleButton
                value="Single"
                aria-label="single trip"
                sx={{
                  "&.Mui-selected": {
                    backgroundColor: "#000",
                    color: "#fff",
                    "&:hover": {
                      backgroundColor: "#000",
                    },
                  },
                  textTransform: "none",
                }}
              >
                Single Trip
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            label="Journey Start Date"
            type="date"
            fullWidth
            value={tripDetails?.journeyDates?.startDate}
            onChange={(e) =>
              setTripDetails((prev: any) => ({
                ...prev,
                journeyDates: {
                  ...prev.journeyDates,
                  startDate: e.target.value,
                },
              }))
            }
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Journey Return Date"
            type="date"
            fullWidth
            value={tripDetails?.journeyDates?.returnDate}
            onChange={(e) =>
              setTripDetails((prev: any) => ({
                ...prev,
                journeyDates: {
                  ...prev.journeyDates,
                  returnDate: e.target.value,
                },
              }))
            }
            InputLabelProps={{ shrink: true }}
            disabled={tripDetails?.tripType === "Single"}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>From Location</InputLabel>
            <Select
              value={tripDetails?.fromLocation}
              label="From Location"
              onChange={(e) => {
                setTripDetails((prev: any) => ({
                  ...prev,
                  fromLocation: e.target.value as string,
                  otherFromLocation:
                    e.target.value === "other" ? prev.otherFromLocation : "",
                }));
              }}
              sx={{ backgroundColor: "#fff" }}
            >
              {locations.map((location: any) => (
                <MenuItem key={location.value} value={location.value}>
                  {location.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {tripDetails?.fromLocation === "other" && (
            <TextField
              fullWidth
              label="Custom From Location"
              value={tripDetails?.otherFromLocation}
              onChange={(e) =>
                setTripDetails((prev: any) => ({
                  ...prev,
                  otherFromLocation: e.target.value,
                }))
              }
              sx={{ mt: 2 }}
            />
          )}
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>To Location</InputLabel>
            <Select
              value={tripDetails?.toLocation}
              label="To Location"
              onChange={(e) => {
                setTripDetails((prev: any) => ({
                  ...prev,
                  toLocation: e.target.value as string,
                  otherToLocation:
                    e.target.value === "other" ? prev.otherToLocation : "",
                }));
              }}
              sx={{ backgroundColor: "#fff" }}
            >
              {locations.map((location: any) => (
                <MenuItem key={location.value} value={location.value}>
                  {location.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {tripDetails?.toLocation === "other" && (
            <TextField
              fullWidth
              label="Custom To Location"
              value={tripDetails?.otherToLocation}
              onChange={(e) =>
                setTripDetails((prev: any) => ({
                  ...prev,
                  otherToLocation: e.target.value,
                }))
              }
              sx={{ mt: 2 }}
            />
          )}
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Journey Start Odometer Reading"
            type="number"
            value={tripDetails?.journeyStartOdometerReading || ""}
            onChange={(e) =>
              setTripDetails((prev: any) => ({
                ...prev,
                journeyStartOdometerReading: e.target.value,
              }))
            }
            InputProps={{
              inputProps: { min: 0 },
            }}
            placeholder="Enter current odometer reading"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Journey End Odometer Reading"
            type="number"
            value={tripDetails?.journeyEndOdometerReading || ""}
            onChange={(e) =>
              setTripDetails((prev: any) => ({
                ...prev,
                journeyEndOdometerReading: e.target.value,
              }))
            }
            InputProps={{
              inputProps: { min: 0 },
            }}
            placeholder="Enter current odometer reading"
          />
        </Grid>
      </Grid>
    </Paper>
  );
};

export default CreateTrip;
